<template>
  <rest-page :fetch-endpoint="fetchEndpoint" :layout="viewPage ? 'tabbed' : ''">
    <template v-if="model">
      <el-alert
        v-if="disabled"
        type="error"
        :closable="false"
        show-icon
        center
        :title="disabledAlertTitle"
      />
      <page-header-new :breadcrumbs="breadcrumbs" :title="title">
        <template slot="actions">
          <el-button
            v-if="disabled && !readonly"
            type="primary"
            :loading="enableLoading"
            @click="openEnableDialog()"
            icon="el-icon-refresh"
            data-qa="enable-sync-button"
          >
            {{ $t('Audiences_RollingSegment_Enable_Sync') }}
          </el-button>
          <el-button
            v-if="!disabled && !readonly && viewPage"
            type="danger"
            plain
            :loading="disableLoading"
            @click="disableSync()"
            icon="el-icon-close"
            data-qa="disable-sync-button"
          >
            {{ $t('Audience_Segment_Disable_Confirm_Button') }}
          </el-button>
          <el-button
            v-if="updatePage"
            type="default"
            @click="cancelUpdate()"
            icon="el-icon-close"
            data-qa="discard-button"
          >
            {{ $t('Button_Discard') }}
          </el-button>

          <rolling-segment-evaluate-dropdown :segment="model" />

          <rolling-segment-settings-dropdown
            v-if="viewPage && !readonly"
            :segment="model"
          />
          <el-button
            v-if="createPage || updatePage || copyPage"
            type="primary"
            @click="beforeSave()"
            icon="ico icon-floppy"
            data-qa="save-button"
          >
            {{ $t('Button_Save') }}
          </el-button>
        </template>
        <template slot="content">
          <segment-counts-top-bar
            v-if="segmentMetrics"
            :metrics="segmentMetrics"
            :type="segmentMetricsType"
          />
        </template>
      </page-header-new>

      <page-tabs v-if="viewPage" :tabs="tabs"></page-tabs>

      <page-content v-if="createPage || updatePage || copyPage">
        <form-section>
          <rest-form
            ref="form"
            :save-endpoint="saveEndpoint"
            :after-save="afterSave"
            :model="model"
            :rules="rules"
            :submit-native="false"
          >
            <rest-form-item
              prop="title"
              :label="$t('Audience_Segment_Form_Title')"
              :hint="$t('Audience_Segment_Form_Title_Hint')"
            >
              <el-input
                v-model="model.title"
                data-qa="rolling-segment-name-input"
              />
            </rest-form-item>
            <rest-form-item
              prop="period"
              :label="$t('Audience_Segment_Form_Rolling_Period_Title')"
              :hint="$t('Audience_Segment_Form_Rolling_Period_Hint')"
            >
              <el-select
                v-model="model.period"
                data-qa="rolling-segment-period-input"
              >
                <el-option
                  v-for="(value, key) in rollingRefreshIntervalLabels"
                  :key="key"
                  :value="Number(key)"
                  :label="$t(value)"
                />
              </el-select>
            </rest-form-item>
            <rest-form-item
              v-if="!useOffsetTimePicker"
              prop="offset"
              style="margin-bottom: 0"
              :label="$t('Audience_Segment_Form_Rolling_Offset_Title')"
              :hint="$t('Audience_Segment_Form_Rolling_Offset_Hint')"
            >
              <el-input-number
                v-model="model.offset"
                :min="0"
                :max="maxOffset"
                data-qa="rolling-segment-offset-input"
              />
            </rest-form-item>
            <rest-form-item
              v-if="useOffsetTimePicker"
              prop="offset"
              style="margin-bottom: 0"
              :label="$t('Audience_Segment_Form_Rolling_OffsetTime_Title')"
              :hint="$t('Audience_Segment_Form_Rolling_OffsetTime_Hint')"
            >
              <el-time-picker
                v-model="offsetTime"
                :picker-options="getTimePickerOptions"
                format="HH:mm"
                value-format="HH:mm"
                data-qa="rolling-segment-offset-time-input"
              />
            </rest-form-item>
          </rest-form>
        </form-section>

        <form-section style="overflow: visible">
          <conditions-editor
            ref="conditions-editor"
            :id="segmentId"
            :conditions="model.conditions"
            v-model="model.conditions"
            data-model-name="Segment"
            data-model-attribute="conditions"
            attribute-list-data-url="/segment/segment/attribute-list-data"
            :preview="false"
          />
        </form-section>

        <form-section v-if="$hasFeature('livingSegments')">
          <div class="destination-form">
            <living-segment-destination-section
              v-if="model.rollingSegmentDestination.length"
            >
              <el-form
                class="form"
                status-icon
                :model="model"
                ref="destinationForm"
              >
                <div
                  v-for="(destination,
                  index) in model.rollingSegmentDestination"
                  class="destination-section"
                  :style="[index === 0 ? { borderTop: 0 } : {}]"
                >
                  <el-form-item
                    :label="$t('Webhook_Stream_Integration_Label')"
                    :prop="
                      `rollingSegmentDestination.${index}.webhook_stream_integration_id`
                    "
                    :rules="[
                      {
                        required: true,
                        message: $t('RestForm_FieldIsRequired'),
                        trigger: ['change']
                      }
                    ]"
                  >
                    <el-select
                      v-model="
                        model.rollingSegmentDestination[index]
                          .webhook_stream_integration_id
                      "
                      filterable
                      remote
                      :remote-method="fetchWsiIntegrations"
                      :loading="wsiIntegrationsLoading"
                      :data-qa="`webhook-stream-integration-select-${index}`"
                    >
                      <el-option
                        v-for="wsi in wsiIntegrations"
                        :label="wsi.name"
                        :value="wsi.id"
                        :key="wsi.id"
                      />
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    :label="
                      $t(
                        'Audiences_RollingSegment_Destination_External_Segment_Id'
                      )
                    "
                    :prop="
                      `rollingSegmentDestination.${index}.external_segment_id`
                    "
                    :rules="[
                      {
                        required: true,
                        message: $t('RestForm_FieldIsRequired'),
                        trigger: ['blur']
                      }
                    ]"
                  >
                    <template v-if="showExternalSegmentPicker">
                      <div
                        v-if="
                          externalSegmentWarningVisible(
                            destination.webhook_stream_integration_id,
                            destination.external_segment_id
                          )
                        "
                        class="external-segment-warning"
                      >
                        {{
                          $t(
                            'Audiences_RollingSegment_Destination_External_Segment_Id_Warning'
                          )
                        }}
                      </div>
                      <el-select
                        v-model="
                          model.rollingSegmentDestination[index]
                            .external_segment_id
                        "
                        filterable
                        :allow-create="
                          externalSegmentAllowCreate(
                            index,
                            destination.webhook_stream_integration_id
                          )
                        "
                        default-first-option
                        clearable
                        :filter-method="
                          query => handleExternalSegmentFilter(index, query)
                        "
                        @visible-change="
                          visible =>
                            handleExternalSegmentVisibleChange(index, visible)
                        "
                        :loading="
                          isExternalSegmentsLoading(
                            destination.webhook_stream_integration_id
                          )
                        "
                        :no-data-text="
                          $t(
                            'Audiences_RollingSegment_Destination_External_Segment_Id_NoMatch'
                          )
                        "
                        :data-qa="`external-segments-id-input-${index}`"
                      >
                        <el-option
                          v-for="segment in filteredExternalSegmentOptions(
                            index,
                            destination.webhook_stream_integration_id
                          )"
                          :key="segment.id"
                          :value="segment.id"
                          :label="`${segment.name} (${segment.id})`"
                        />
                      </el-select>
                    </template>
                    <el-input
                      v-else
                      v-model="
                        model.rollingSegmentDestination[index]
                          .external_segment_id
                      "
                      :data-qa="`external-segments-id-input-${index}`"
                    />
                  </el-form-item>
                  <div style="display: flex; justify-content: center">
                    <el-button
                      type="primary"
                      plain
                      @click="removeDestination(index)"
                      data-qa="rolling-segments-remove-destination-button"
                    >
                      {{
                        $t(
                          'Audiences_RollingSegment_Destination_RemoveDestination'
                        )
                      }}
                    </el-button>
                  </div>
                </div>
              </el-form>
            </living-segment-destination-section>
            <el-tooltip
              :disabled="!destinationLimitReached"
              :content="
                $t('Audiences_RollingSegment_Destination_Limit_Reached')
              "
              placement="bottom"
              effect="light"
              data-qa="rolling-segments-destination-limit-reached-tooltip"
            >
              <el-button
                icon="el-icon-circle-plus-outline"
                type="primary"
                class="addDestinationButton"
                :disabled="destinationLimitReached"
                @click="addDestination"
                data-qa="rolling-segments-add-destination-button"
              >
                {{
                  $t('Audiences_RollingSegment_Destination_AddNewDestination')
                }}
              </el-button>
            </el-tooltip>
          </div>
        </form-section>
      </page-content>

      <enable-rolling-segment-dialog
        v-if="showEnableDialog"
        :segment="model"
        :visible="showEnableDialog"
        @enable="confirmEnable"
        @close="showEnableDialog = false"
      />
      <disable-rolling-segment-dialog
        v-if="showDisableDialog"
        :segment="model"
        :campaigns="disableDialogCampaigns"
        :visible="showDisableDialog"
        :loading="disableSubmitting"
        @disable="confirmDisable"
        @close="closeDisableDialog"
      />
      <calculate-size-dialog v-if="showCalculateSizeDialog" />
      <channel-reach-dialog v-if="showChannelsReachDialog" />
      <channel-reach-configure-dialog v-if="showChannelsReachConfigureDialog" />
      <members-preview-loading-dialog v-if="showPreviewMembersDialog" />
      <members-preview-list-dialog
        v-if="showPreviewMembersListDialog"
        :profile-ids="members_preview_result.task.recipients"
      />
      <members-export-dialog v-if="showExportMembersDialog" />
      <members-export-run-dialog
        v-if="showExportMembersRunDialog"
        :task="export_result"
      />
      <members-export-add-attributes-dialog
        v-if="showExportMembersAttributesDialog"
      />
    </template>
  </rest-page>
</template>

<script>
import { projectEndpoint, projectRoute, customConfirm } from '@/core/helpers'
import { EventBus } from '@/core/EventBus'
import RestApi from '@/core/services/RestApi'
import PageHeaderNew from '@/common/components/page/PageHeaderNew'
import PageContent from '@/common/components/page/PageContent'
import RestPage from '@/common/components/rest/RestPage'
import RestForm from '@/common/components/rest/RestForm'
import ConditionsEditor from '@/common/components/ConditionsEditor'
import FormSection from '@/common/components/form/FormSection'
import SegmentCountsTopBar from '@/modules/audiences/components/SegmentCountsTopBar'
import ChannelReachDialog from '@/modules/audiences/components/ChannelReachDialog'
import MembersPreviewListDialog from '@/modules/audiences/components/MembersPreviewListDialog'
import CalculateSizeDialog from '@/modules/audiences/components/CalculateSizeDialog'
import MembersPreviewLoadingDialog from '@/modules/audiences/components/MembersPreviewLoadingDialog'
import MembersExportDialog from '@/modules/audiences/components/MembersExportDialog'
import MembersExportRunDialog from '@/modules/audiences/components/MembersExportRunDialog'
import MembersExportAddAttributesDialog from '@/modules/audiences/components/MembersExportAddAttributesDialog'
import PageTabs from '@/common/components/page/PageTabs'
import RestFormItem from '@/common/components/rest/RestFormItem'
import RollingSegmentSettingsDropdown from '@/modules/audiences/components/RollingSegmentSettingsDropdown'
import RollingSegmentEvaluateDropdown from '@/modules/audiences/components/RollingSegmentEvaluateDropdown'
import {
  segmentSyncStatus,
  isSegmentDisabled
} from '@/modules/audiences/enum/SegmentSyncStatusEnum'
import EnableRollingSegmentDialog from '@/modules/audiences/components/EnableRollingSegmentDialog'
import DisableRollingSegmentDialog from '@/modules/audiences/components/DisableRollingSegmentDialog'
import ChannelReachConfigureDialog from '@/modules/audiences/components/ChannelReachConfigureDialog'
import LivingSegmentDestinationSection from '@/modules/audiences/components/LivingSegmentDestinationSection'
import { $hasFeature } from '@/core/utils/VueAcl'
import {
  rollingRefreshIntervalLabels,
  rollingRefreshIntervalEnum
} from '@/modules/audiences/enum/RollingRefreshIntervalEnums'
import iframeTitleMixin from '@/common/mixins/iframeTitleMixin'
import externalSegmentsMixin from '@/modules/audiences/mixins/externalSegmentsMixin'

export default {
  name: 'RollingSegmentPage',

  mixins: [iframeTitleMixin, externalSegmentsMixin],

  components: {
    EnableRollingSegmentDialog,
    DisableRollingSegmentDialog,
    LivingSegmentDestinationSection,
    ChannelReachConfigureDialog,
    RollingSegmentEvaluateDropdown,
    RollingSegmentSettingsDropdown,
    RestFormItem,
    FormSection,
    ConditionsEditor,
    RestForm,
    RestPage,
    PageContent,
    SegmentCountsTopBar,
    ChannelReachDialog,
    MembersPreviewListDialog,
    CalculateSizeDialog,
    MembersPreviewLoadingDialog,
    MembersExportDialog,
    MembersExportRunDialog,
    MembersExportAddAttributesDialog,
    PageTabs,
    PageHeaderNew
  },

  data() {
    return {
      model: null,
      showCalculateSizeDialog: false,
      showPreviewMembersDialog: false,
      showPreviewMembersListDialog: false,
      showChannelsReachDialog: false,
      showChannelsReachConfigureDialog: false,
      showExportMembersDialog: false,
      showExportMembersRunDialog: false,
      showExportMembersAttributesDialog: false,
      channelsReachParams: {},
      exportParams: {},
      taskId: null,
      calculate_size_result: {},
      channel_reach_result: {},
      export_result: {},
      members_preview_result: null,
      taskLoop: null,
      segmentMetrics: null,
      segmentMetricsType: null,
      existingRollingSegmentDestinationCount: 0,
      newRollingSegmentDestinationCount: 0,
      destinationErrors: false,
      segmentSyncStatus,
      showEnableDialog: false,
      enableLoading: false,
      disableLoading: false,
      showDisableDialog: false,
      disableDialogCampaigns: [],
      disableSubmitting: false,
      lastModifiedData: {
        modifiedStartTime: null,
        lastModifiedTime: null,
        lastModifier: null
      },
      wsiIntegrationsLoading: false,
      selectedWsiIntegration: '',
      wsiIntegrations: [],
      rollingRefreshIntervalLabels,
      rollingRefreshIntervalEnum,
      externalSegmentQueries: {}
    }
  },

  methods: {
    handleExternalSegmentFilter(index, query) {
      this.$set(this.externalSegmentQueries, index, query)
    },

    handleExternalSegmentVisibleChange(index, visible) {
      if (!visible) {
        this.$set(this.externalSegmentQueries, index, '')
      }
    },

    filteredExternalSegmentOptions(index, integrationId) {
      const query = (this.externalSegmentQueries[index] || '')
        .toLowerCase()
        .trim()
      const options = this.externalSegmentOptions(integrationId)
      if (!query) {
        return options
      }
      return options.filter(
        segment =>
          segment.name.toLowerCase().includes(query) ||
          String(segment.id)
            .toLowerCase()
            .includes(query)
      )
    },

    externalSegmentAllowCreate(index, integrationId) {
      return (
        this.filteredExternalSegmentOptions(index, integrationId).length === 0
      )
    },

    async beforeSave() {
      if (this.updatePage) {
        await this.getLastModifiedTime()
        if (
          this.lastModifiedData.modifiedStartTime <
          this.lastModifiedData.lastModifiedTime
        ) {
          await this.showOverwriteConfirmation()
        }
      }

      if (!this.model.rollingSegmentDestination.length) {
        await this.save()
        return
      }

      await this.$refs.destinationForm.validate(valid => {
        if (!valid) {
          return
        }
        this.save()
      })
    },

    getLastModifiedTime() {
      return new Promise((resolve, reject) => {
        RestApi(
          projectEndpoint({
            method: 'get',
            url: `segments/${this.rollingSegmentId}`,
            params: {
              fields: 'last_modified_time,last_modifier',
              expand: 'last_modified_time,last_modifier'
            }
          })
        )
          .then(({ data }) => {
            this.lastModifiedData.lastModifiedTime = data.last_modified_time
            this.lastModifiedData.lastModifier = data.last_modifier
            resolve()
          })
          .catch(error => {
            reject(error)
          })
      })
    },

    async save() {
      if (!this.createPage) {
        await this.showConfirmDialogOnUpdate()
      }
      if (this.copyPage) {
        this.model.copy = true
      }
      this.model.conditions = this.$refs['conditions-editor'].serialize()
      this.$refs['form'].save()
    },

    async showOverwriteConfirmation() {
      return new Promise(resolve => {
        let overwriteMessage = this.$t(
          'Audience_Segment_Overwrite_SegmentMessage',
          {
            user: this.lastModifiedData.lastModifier.name,
            minutes: this.lastModifiedDuration(),
            noun: this.lastModifiedDuration() > 1 ? 'minutes' : 'minute'
          }
        )
        this.$confirm(overwriteMessage, '', {
          confirmButtonText: this.$t(
            'CampaignForm_ReviewDialog_Overwrite_Changes'
          ),
          cancelButtonText: this.$t('Campaign_Dialog_Button_Cancel'),
          type: 'warning',
          dangerouslyUseHTMLString: true
        }).then(() => {
          resolve()
        })
      })
    },

    async showConfirmDialogOnUpdate() {
      return new Promise(resolve => {
        customConfirm(
          this.$t('Audience_Segment_Update_Rolling_Segment'),
          this.$t('Audience_Segment_Update_Rolling_Segment_Text'),
          this.$t('Button_Save'),
          undefined,
          undefined,
          ' custom-confirm-ce'
        ).then(() => {
          resolve()
        })
      })
    },

    afterSave(model) {
      this.$router.push(
        projectRoute({
          name: 'AudienceRollingSegmentOverview',
          query: { id: model.id }
        })
      )
    },

    cancelUpdate() {
      this.$router.push(
        projectRoute({
          name: 'AudienceRollingSegmentOverview',
          query: { id: this.model.id }
        })
      )
    },

    calculateSize(scope, samplingAllowed) {
      let evaluateData = this.evaluateData()
      evaluateData.scope = scope || 'all'
      evaluateData.samplingAllowed = !!samplingAllowed
      RestApi.post(this.countEndpoint.url, evaluateData)
        .then(({ data }) => {
          this.taskId = data.task_id
          this.fetchSizeTask()
          this.taskLoop = setInterval(() => {
            this.fetchSizeTask()
          }, 2000)
        })
        .catch(error => {
          this.showCalculateSizeDialog = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    calculateSizeShow(scope, samplingAllowed) {
      this.showCalculateSizeDialog = true
      this.calculateSize(scope, samplingAllowed)
    },

    calculateSizeHide() {
      this.showCalculateSizeDialog = false
    },

    calculateSizeCancel() {
      this.calculateSizeHide()
      clearInterval(this.taskLoop)
      this.deleteCurrentTask()
    },

    calculateSizeResult(scope, data) {
      this.segmentMetricsType = scope
      this.segmentMetrics = data
    },

    channelsReach(params, samplingAllowed) {
      let evaluateData = { ...this.evaluateData(), ...params }
      evaluateData.scope = 'channels'
      evaluateData.samplingAllowed = !!samplingAllowed
      RestApi.post(this.countEndpoint.url, evaluateData)
        .then(({ data }) => {
          this.taskId = data.task_id
          this.fetchReachTask()
          this.taskLoop = setInterval(() => {
            this.fetchReachTask()
          }, 2000)
        })
        .catch(error => {
          this.showChannelsReachDialog = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    channelsReachConfigure() {
      this.showChannelsReachConfigureDialog = true
    },

    channelsReachShow(params, samplingAllowed) {
      this.channelsReachParams = params
      this.showChannelsReachDialog = true
      this.showChannelsReachConfigureDialog = false
      this.channelsReach(params, samplingAllowed)
    },

    channelsReachHide() {
      this.showChannelsReachDialog = false
      this.showChannelsReachConfigureDialog = false
    },

    channelsReachCancel() {
      this.channelsReachHide()
      clearInterval(this.taskLoop)
      this.deleteCurrentTask()
    },

    channelsReachResult(data) {
      this.segmentMetricsType = 'channels'
      this.segmentMetrics = data
    },

    recalculateExactSize() {
      if (this.segmentMetricsType === 'channels') {
        this.channelsReachShow(this.channelsReachParams, false)
      } else {
        this.calculateSizeShow(this.segmentMetricsType, false)
      }
    },

    exportMembers() {
      let evaluateData = {
        ...this.evaluateData(),
        ...this.exportParams
      }
      RestApi.post(this.exportEndpoint.url, evaluateData)
        .then(({ data }) => {
          this.taskId = data.task_id
          this.fetchExportTask()
          this.taskLoop = setInterval(() => {
            this.fetchExportTask()
          }, 2000)
        })
        .catch(error => {
          this.showExportMembersDialog = false
          this.showExportMembersAttributesDialog = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    exportMembersShow() {
      this.showExportMembersDialog = true
    },

    exportMembersHide() {
      this.showExportMembersDialog = false
    },

    exportMembersCancel() {
      this.exportMembersHide()
      clearInterval(this.taskLoop)
      this.deleteCurrentTask()
    },

    exportMembersRunShow(params) {
      Object.assign(this.exportParams, params)
      this.showExportMembersDialog = false
      this.showExportMembersRunDialog = true
      this.exportMembers()
    },

    exportMembersRunHide() {
      this.showExportMembersRunDialog = false
      this.exportParams = {}
      this.export_result = {}
      clearInterval(this.taskLoop)
      this.taskId = null
    },

    exportMembersAddAttributesShow(params) {
      this.exportParams = params
      this.showExportMembersAttributesDialog = true
    },

    exportMembersAddAttributesHide() {
      this.showExportMembersAttributesDialog = false
    },

    membersPreview() {
      let evaluateData = this.evaluateData()
      evaluateData.limit = 25

      RestApi.post(this.previewEndpoint.url, evaluateData)
        .then(({ data }) => {
          if (this.showPreviewMembersDialog) {
            this.taskId = data.task_id
            this.fetchPreviewTask()
            this.taskLoop = setInterval(() => {
              this.fetchPreviewTask()
            }, 2000)
          }
        })
        .catch(error => {
          this.showPreviewMembersDialog = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    membersPreviewShow() {
      this.showPreviewMembersDialog = true
      this.membersPreview()
    },

    membersPreviewHide() {
      this.showPreviewMembersDialog = false
    },

    membersPreviewListClose() {
      this.showPreviewMembersListDialog = false
      this.members_preview_result = null
    },

    membersPreviewListShow() {
      this.showPreviewMembersListDialog = true
    },

    membersPreviewCancel() {
      this.membersPreviewHide()
      clearInterval(this.taskLoop)
      this.deleteCurrentTask()
    },

    fetchSizeTask() {
      RestApi.get(this.taskEndpoint.url).then(({ data }) => {
        this.calculate_size_result = data
      })
    },

    fetchReachTask() {
      RestApi.get(this.taskEndpoint.url).then(({ data }) => {
        this.channel_reach_result = data
      })
    },

    fetchPreviewTask() {
      RestApi.get(this.taskEndpoint.url).then(({ data }) => {
        this.members_preview_result = data
      })
    },

    fetchExportTask() {
      RestApi.get(this.taskEndpoint.url).then(({ data }) => {
        this.export_result = data
      })
    },

    deleteCurrentTask() {
      RestApi.delete(this.taskEndpoint.url)
    },

    evaluateData() {
      if (this.$refs['conditions-editor']) {
        return {
          conditions: this.$refs['conditions-editor'].serialize(),
          id: this.segmentId
        }
      } else {
        return {
          conditions: this.model.conditions,
          id: this.segmentId
        }
      }
    },

    handleTaskStatus(task, success, error) {
      switch (task.status) {
        case 2:
          clearInterval(this.taskLoop)
          success()
          break

        case 3:
          this.$message({
            message: this.$t('Audiences_Segment_Alert_Error'),
            type: 'error'
          })
          clearInterval(this.taskLoop)
          error()
          break

        case 4:
          this.$message({
            message: this.$t('Audiences_Segment_Alert_Fatal'),
            type: 'error'
          })
          clearInterval(this.taskLoop)
          error()
          break
      }
    },

    addDestination() {
      this.model.rollingSegmentDestination.push({
        name: '',
        add_endpoint: '',
        remove_endpoint: '',
        webhook_stream_integration_id: null,
        external_segment_id: ''
      })
      this.newRollingSegmentDestinationCount++
    },

    removeDestination(index) {
      if (this.model.rollingSegmentDestination[index].id) {
        this.existingRollingSegmentDestinationCount--
      } else {
        this.newRollingSegmentDestinationCount--
      }
      this.model.rollingSegmentDestination.splice(index, 1)
    },

    openEnableDialog() {
      this.showEnableDialog = true
    },

    confirmEnable({ full_sync }) {
      this.enableLoading = true
      RestApi(
        projectEndpoint({
          method: 'post',
          url: `segments/${this.rollingSegmentId}/enable`,
          data: { full_sync }
        })
      )
        .then(() => {
          this.model.status = segmentSyncStatus.NOT_IN_PROGRESS
          this.$message({
            message: this.$t('Audience_Segment_Enabled_Success'),
            type: 'success'
          })
          this.showEnableDialog = false
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
        .finally(() => {
          this.enableLoading = false
        })
    },

    async disableSync() {
      this.disableLoading = true
      try {
        const { data: deps } = await RestApi(
          projectEndpoint({
            method: 'get',
            url: `segments/${this.rollingSegmentId}/dependencies`
          })
        )
        this.disableDialogCampaigns = (deps && deps.campaigns) || []
        this.showDisableDialog = true
      } catch (e) {
        this.$message({
          message: e.message,
          type: 'error'
        })
      } finally {
        this.disableLoading = false
      }
    },

    closeDisableDialog() {
      this.showDisableDialog = false
      this.disableDialogCampaigns = []
    },

    async confirmDisable() {
      this.disableSubmitting = true
      try {
        await RestApi(
          projectEndpoint({
            method: 'post',
            url: `segments/${this.rollingSegmentId}/disable`
          })
        )

        this.model.status = segmentSyncStatus.MANUALLY_DISABLED

        this.$message({
          message: this.$t('Audience_Segment_Disabled_Success'),
          type: 'success'
        })
        this.closeDisableDialog()
      } catch (e) {
        this.$message({
          message: e.message,
          type: 'error'
        })
      } finally {
        this.disableSubmitting = false
      }
    },

    lastModifiedDuration() {
      const lastModified = Number(this.lastModifiedData.lastModifiedTime)
      const durationInSeconds = Math.floor(Date.now() / 1000) - lastModified

      return Math.floor(durationInSeconds / 60 + 1)
    },

    fetchWsiIntegrations(query = null) {
      this.wsiIntegrationsLoading = true

      let params = {
        type: 'webhookstream',
        active: 1,
        sort: 'name'
      }
      if (query !== null && query !== '') {
        params.name = query
      }

      let endpoint = projectEndpoint({
        method: 'get',
        url: 'integrations',
        params: params
      })

      RestApi(endpoint)
        .then(({ data }) => {
          this.wsiIntegrations = data
          this.wsiIntegrationsLoading = false
        })
        .catch(error => {
          this.loadingIntegrations = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    }
  },

  created() {
    if (!this.rollingSegmentId) {
      this.model = {
        title: '',
        period: 0,
        type: 1,
        conditions: {
          operator: 'AND',
          '0': {
            operator: 'AND',
            '0': {}
          }
        },
        rollingSegmentDestination: []
      }
    }
    if (!this.viewPage) {
      this.fetchWsiIntegrations()
    }
  },

  computed: {
    segmentId() {
      return this.model ? this.model.id : null
    },
    rollingSegmentId() {
      return this.$route.query.id
    },

    updatePage() {
      return this.$route.name === 'AudienceRollingSegmentUpdate'
    },

    createPage() {
      return this.$route.name === 'AudienceRollingSegmentCreate'
    },

    copyPage() {
      return this.$route.name === 'AudienceRollingSegmentCopy'
    },

    viewPage() {
      return (
        this.currentRoute === 'AudienceRollingSegmentOverview' ||
        this.currentRoute === 'AudienceRollingSegmentEvaluations' ||
        this.currentRoute === 'AudienceRollingSegmentSharing' ||
        this.currentRoute === 'AudienceRollingSegmentDestinations'
      )
    },

    fetchEndpoint() {
      return [
        this.fetchRollingSegmentEndpoint,
        this.fetchRollingSegmentDestinationCount
      ]
    },

    fetchRollingSegmentEndpoint() {
      if (this.rollingSegmentId) {
        return projectEndpoint({
          method: 'get',
          url: `segments/${this.rollingSegmentId}`,
          params: {
            expand: 'rollingSegmentDestination'
          },
          handler: data => {
            this.model = data

            if (this.copyPage) {
              this.model.title += ' ' + this.$t('Audiences_Segment_Label_Copy')
            }
          }
        })
      } else {
        return null
      }
    },

    fetchRollingSegmentDestinationCount() {
      return projectEndpoint({
        method: 'get',
        url: 'rolling-segment-destination/count-all',
        handler: data => {
          this.existingRollingSegmentDestinationCount = data
        }
      })
    },

    saveEndpoint() {
      if (this.rollingSegmentId && !this.copyPage) {
        return projectEndpoint({
          method: 'patch',
          url: `segments/${this.rollingSegmentId}`
        })
      } else {
        return projectEndpoint({
          method: 'post',
          url: `segments`
        })
      }
    },

    countEndpoint() {
      return projectEndpoint({
        url: `segment-evaluate/count`
      })
    },

    previewEndpoint() {
      return projectEndpoint({
        url: `segment-evaluate/preview`
      })
    },

    exportEndpoint() {
      return projectEndpoint({
        url: `segment-evaluate/export`
      })
    },

    taskEndpoint() {
      return projectEndpoint({
        url: `task-histories/${this.taskId}?expand=task`
      })
    },

    rules() {
      return {
        title: {
          required: true
        },
        offset: {
          required: true
        }
      }
    },

    currentRoute() {
      return this.$router.currentRoute.name
    },

    title() {
      if (this.createPage) {
        return this.$t('Audience_Segment_CreateRolling_Title')
      }
      return this.model ? this.model.title : null
    },

    breadcrumbs() {
      return [
        {
          label: 'Data'
        },
        {
          label: this.$t('Audiences_Breadcrumb_Audiences'),
          route: projectRoute({
            name: 'AudienceRollingSegmentList'
          })
        },
        {
          label: this.createPage
            ? this.$t('Audiences_Breadcrumb_CreateRollingSegment')
            : this.$t('Audiences_Breadcrumb_RollingSegment')
        }
      ]
    },

    tabs() {
      let tabs = [
        {
          title: this.$t('Audience_Segment_Overview_Tab'),
          route: projectRoute({
            name: 'AudienceRollingSegmentOverview',
            query: {
              id: this.$route.query.id
            }
          })
        },
        {
          title: this.$t('Audiences_SegmentEvaluationsTab'),
          route: projectRoute({
            name: 'AudienceRollingSegmentEvaluations',
            query: {
              id: this.$route.query.id
            }
          })
        }
      ]

      if (
        $hasFeature('livingSegments') &&
        this.model.rollingSegmentDestination.length
      ) {
        tabs.push({
          title: 'Destinations',
          route: projectRoute({
            name: 'AudienceRollingSegmentDestinations',
            query: {
              id: this.$route.query.id
            }
          })
        })
      }
      return tabs
    },

    readonly() {
      return !this.$can('updateProjectItem')
    },

    disabled() {
      return isSegmentDisabled(this.model.status)
    },

    disabledAlertTitle() {
      if (Number(this.model.status) === segmentSyncStatus.MANUALLY_DISABLED) {
        return this.$t('Audience_Segment_Disable_Banner_Manual')
      }
      return this.$t('Audiences_RollingSegment_Too_Complex_Error')
    },

    destinationLimitReached() {
      let totalDestinations =
        this.existingRollingSegmentDestinationCount +
        this.newRollingSegmentDestinationCount

      return (
        totalDestinations >=
          this.$store.state.app.project
            .max_rolling_segments_destination_count ||
        this.model.rollingSegmentDestination.length >=
          this.$store.state.app.project
            .max_destinations_per_rolling_segment_count
      )
    },

    offsetTime: {
      get() {
        const hours = Math.floor(this.model.offset / 60)
        const minutes = this.model.offset - hours * 60
        return (
          hours.toString().padStart(2, '0') +
          ':' +
          minutes.toString().padStart(2, '0')
        )
      },
      set(value) {
        let hours, minutes
        ;[hours, minutes] = value.split(':')
        this.model.offset = Number(hours) * 60 + Number(minutes)
      }
    },
    maxOffset() {
      switch (this.model.period) {
        case rollingRefreshIntervalEnum.QUARTER_HOUR:
          return 14
        case rollingRefreshIntervalEnum.HOUR:
          return 59
        case rollingRefreshIntervalEnum.HALF_DAY:
          return 719
        case rollingRefreshIntervalEnum.DAY:
          return 1439
      }
    },
    useOffsetTimePicker() {
      return (
        this.model.period === rollingRefreshIntervalEnum.HALF_DAY ||
        this.model.period === rollingRefreshIntervalEnum.DAY
      )
    },
    getTimePickerOptions() {
      return this.model.period === rollingRefreshIntervalEnum.HALF_DAY
        ? { selectableRange: '00:00:00 - 11:59:59' }
        : { selectableRange: '00:00:00 - 23:59:59' }
    },

    showExternalSegmentPicker() {
      return (
        this.$hasReleaseFlag('external-segment-picker') &&
        this.$hasFeature('externalSegmentPicker')
      )
    },

    usedWebhookIntegrationIds() {
      if (!this.model) {
        return []
      }
      return this.model.rollingSegmentDestination
        .map(destination => destination.webhook_stream_integration_id)
        .filter(Boolean)
    }
  },

  watch: {
    calculate_size_result: {
      handler(result) {
        if (result) {
          this.handleTaskStatus(
            result,
            () => {
              EventBus.$emit(
                'segment-metrics-size-change',
                result.task.scope,
                result.task.count
              )
              this.calculateSizeHide()
            },
            () => {
              this.calculateSizeHide()
            }
          )
        }
      },
      deep: true
    },

    channel_reach_result: {
      handler(result) {
        this.handleTaskStatus(
          result,
          () => {
            EventBus.$emit('segment-metrics-channel-change', result.task.count)
            this.channelsReachHide()
          },
          () => {
            this.channelsReachHide()
          }
        )
      },
      deep: true
    },

    members_preview_result: {
      handler(result) {
        if (!result) {
          return
        }
        this.handleTaskStatus(
          result,
          () => {
            this.membersPreviewHide()
            this.membersPreviewListShow()
          },
          () => {
            this.membersPreviewHide()
          }
        )
      },
      deep: true
    },

    export_result: {
      handler(result) {
        if (result) {
          this.handleTaskStatus(
            result,
            () => {},
            () => {
              this.exportMembersHide()
              this.exportMembersRunHide()
            }
          )
        }
      },
      deep: true
    },
    'model.period': function() {
      if (this.model.offset > this.maxOffset) {
        this.model.offset = this.maxOffset
      }
    },

    usedWebhookIntegrationIds: {
      immediate: true,
      handler(integrationIds) {
        if (this.showExternalSegmentPicker) {
          this.ensureExternalSegmentsLoaded(integrationIds)
        }
      }
    }
  },

  mounted() {
    this.lastModifiedData.modifiedStartTime = Math.floor(Date.now() / 1000)
    EventBus.$on('calculate-size-show', this.calculateSizeShow)
    EventBus.$on('calculate-size-hide', this.calculateSizeHide)
    EventBus.$on('calculate-size-cancel', this.calculateSizeCancel)
    EventBus.$on('channels-reach-configure', this.channelsReachConfigure)
    EventBus.$on('channels-reach-show', this.channelsReachShow)
    EventBus.$on('channels-reach-hide', this.channelsReachHide)
    EventBus.$on('channels-reach-cancel', this.channelsReachCancel)
    EventBus.$on('recalculate-exact-size', this.recalculateExactSize)
    EventBus.$on('members-preview-show', this.membersPreviewShow)
    EventBus.$on('members-preview-hide', this.membersPreviewHide)
    EventBus.$on('members-preview-list-close', this.membersPreviewListClose)
    EventBus.$on('members-preview-cancel', this.membersPreviewCancel)
    EventBus.$on('export-members-show', this.exportMembersShow)
    EventBus.$on('export-members-hide', this.exportMembersHide)
    EventBus.$on('export-members-run-show', this.exportMembersRunShow)
    EventBus.$on('export-members-run-hide', this.exportMembersRunHide)
    EventBus.$on('export-members-cancel', this.exportMembersCancel)
    EventBus.$on(
      'export-members-add-attributes-show',
      this.exportMembersAddAttributesShow
    )
    EventBus.$on(
      'export-members-add-attributes-hide',
      this.exportMembersAddAttributesHide
    )
    EventBus.$on('segment-metrics-size-change', this.calculateSizeResult)
    EventBus.$on('segment-metrics-channel-change', this.channelsReachResult)
  },

  destroyed() {
    EventBus.$off('calculate-size-show')
    EventBus.$off('calculate-size-hide')
    EventBus.$off('calculate-size-cancel')
    EventBus.$off('channels-reach-configure')
    EventBus.$off('channels-reach-show')
    EventBus.$off('channels-reach-hide')
    EventBus.$off('channels-reach-cancel')
    EventBus.$off('recalculate-exact-size')
    EventBus.$off('members-preview-show')
    EventBus.$off('members-preview-hide')
    EventBus.$off('members-preview-list-close')
    EventBus.$off('members-preview-cancel')
    EventBus.$off('export-members-show')
    EventBus.$off('export-members-hide')
    EventBus.$off('export-members-run-show')
    EventBus.$off('export-members-run-hide')
    EventBus.$off('export-members-add-attributes-show')
    EventBus.$off('export-members-add-attributes-hide')
    EventBus.$off('export-members-cancel')
  }
}
</script>
<style scoped>
.destination-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.destination-form .form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.addDestinationButton {
  border: 1px solid #d9dada;
  background: #f8fafc;
  width: 100%;
  color: #1d87c8;
  font-weight: normal;
  height: 46px;
}

.destination-section {
  padding: 20px;
  background-color: #f8fafc;
  border: 1px solid #ebeef5;
}

.external-segment-warning {
  color: #a47d06;
  font-size: 12px;
  margin-bottom: 6px;
}
</style>



// WEBPACK FOOTER //
// src/modules/audiences/pages/RollingSegmentPage.vue